import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../../hooks/useTheme';
import { X, Check, ChevronUp, ChevronDown, User, AlertCircle, XSquare } from 'lucide-react';
import { getInterviewCommentsThunk } from '../../store/thunks/InterviewThunk';
import { resetInterviewCommentsState } from '../../store/slices/interviewCommentsSlice';
import { formatRelativeDate } from '../../utils/misc';
import ReportComment from './ReportComment';


const profile_photos_host = process.env.REACT_APP_PROFILE_PHOTOS_HOST;


export default function AddRemarkModal({ isOpen, onClose, onSubmit, candidateName, interviewId }) {
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [remark, setRemark] = useState('');
  const [showPrevious, setShowPrevious] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Get comments from Redux
  const interviewComments = useSelector(
    (state) => state.interviewCommentsSlice.comments
  );
  const commentsLoading = useSelector(
    (state) => state.interviewCommentsSlice.loading
  );

  // Reset local state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRemark('');
      setShowWarning(false);
      setIsShaking(false);
      setIsSubmitted(false);
    }
  }, [isOpen]);

  // Fetch comments when modal opens or interviewId changes
  useEffect(() => {
    if (isOpen && interviewId) {
      // Clear the shared comments first so the previous candidate's remarks
      // don't flash while this candidate's remarks load.
      dispatch(resetInterviewCommentsState());
      dispatch(getInterviewCommentsThunk(interviewId));
    }
  }, [isOpen, interviewId, dispatch]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (remark.trim() && !isSubmitted) {
      onSubmit?.(remark);
      setRemark('');
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        onClose();
      }, 2000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[99999999]">
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-6px); }
          30%       { transform: translateX(6px); }
          45%       { transform: translateX(-5px); }
          60%       { transform: translateX(5px); }
          75%       { transform: translateX(-3px); }
          90%       { transform: translateX(3px); }
        }
        .shake { animation: shake 0.5s ease; }
      `}</style>
      <div
        className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20 bg-opacity-50"
        style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        onMouseDown={() => {
          if (remark.trim()) {
            setShowWarning(true);
            setIsShaking(true);
            setTimeout(() => setIsShaking(false), 500);
          } else {
            onClose();
          }
        }}
      />

      <div
        className="w-full max-w-lg p-5 rounded-xl shadow-lg relative bg-bgSecondary max-h-[80vh] overflow-y-auto"
        style={{
          background: isDarkMode
            ? 'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))'
            : '',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(50px)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="dark:text-[#C1A0D0] text-textPrimary text-base font-medium">
            Add Remark {candidateName && `for ${candidateName}`}
          </h3>
          <button onClick={onClose}>
            <X className="h-5 w-5 dark:text-[#C1A0D0] text-textPrimary" />
          </button>
        </div>

        

        {/* Textarea row */}
        <div className="flex gap-3 mb-1.5">
          <textarea
            value={remark}
            onChange={(e) => {
              setRemark(e.target.value);
              if (showWarning) setShowWarning(false);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Write your remarks..."
            maxLength={250}
            className={`flex-1 min-h-[20px] resize-none rounded-lg border bg-bgPrimary dark:bg-bgSecondary text-textPrimary dark:text-gray-300 placeholder:text-textTertiary text-base px-3 py-2.5 focus:outline-none focus:ring-1 ${
              showWarning ? 'border-red-500' : 'border-gray-400 dark:border-gray-500 focus:ring-purple-400'
            } ${isShaking ? 'shake' : ''}`}
          />
        </div>
        <p className="text-xs text-textTertiary dark:text-gray-400 ml-1">
          {remark.length >= 250 ? 'Character limit reached (250)' : `${remark.length}/250 characters`}
        </p>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 mb-5">
          <button
            onClick={handleSubmit}
            disabled={!isSubmitted && (!remark.trim() || commentsLoading)}
            className={`px-6 py-2 rounded-full text-sm ${
              isSubmitted
                ? `${isDarkMode === 'dark' ? 'border-btn-dark' : 'border-btn !text-textPrimary'} !py-1.5 mt-1 mb-1 w-fit max-w-[200px]`
                : 'text-white bg-gradient-to-r from-pink-500 to-blue-500 disabled:bg-none dark:disabled:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-100 disabled:text-white/50'
            }`}
          >
            {isSubmitted ? 'Submitted' : 'Submit'}
          </button>
        </div>

        {/* Previous remarks section - shown first */}
        {interviewComments.length > 0 && (
          <div className="mb-1">
            <button
              onClick={() => setShowPrevious(!showPrevious)}
              className="flex items-center w-full text-sm font-medium text-textPrimary mb-1"
            >
              {/* Left text */}
              <span className="whitespace-nowrap dark:text-gray-400">
                Previous Remarks ({interviewComments.length})
              </span>

              {/* Divider line */}
              <div className="flex-1 mx-3 h-[1px] bg-gray-200 dark:bg-white/10" />

              {/* Right chevron */}
              {showPrevious ? (
                <ChevronUp className="h-4 w-4 flex-shrink-0 transition-transform duration-250" />
              ) : (
                <ChevronDown className="h-4 w-4 flex-shrink-0 transition-transform duration-250" />
              )}
            </button>

            {showPrevious && (
              <div className="flex flex-col gap-3.5 max-h-[200px] overflow-y-auto">
                {[...interviewComments].reverse().map((comment, i) => {
                  const link = comment.isAdmin
                    ? `${profile_photos_host}/OrganizationAdmins/${comment.givenBy}_${comment.details?.profile}`
                    : `${profile_photos_host}/HRProfiles/${comment.givenBy}_${comment.details?.profile}`;
                  return (
                    <ReportComment
                      key={i}
                      comment={{ ...comment, profilePhotoLink: link }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}